import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, overflow: "hidden", height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)", transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: { "&:hover": { backgroundColor: "#f0fff4" }, "&:nth-of-type(even)": { backgroundColor: "#fafafa" } },
}));

const PERIOD_OPTIONS = [
  { label: "Próximos 7 dias", value: "next7" },
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Personalizado", value: "custom" },
];

const STATUS_CONFIG = {
  scheduled:  { label: "Agendado",   bg: "#ebf8ff", color: "#2b6cb0",  pieColor: "#4299e1" },
  confirmed:  { label: "Confirmado", bg: "#f0fff4", color: "#276749",  pieColor: "#38a169" },
  completed:  { label: "Concluído",  bg: "#fffff0", color: "#744210",  pieColor: "#d69e2e" },
  cancelled:  { label: "Cancelado",  bg: "#fff5f5", color: "#c53030",  pieColor: "#e53e3e" },
  no_show:    { label: "Não Compareceu", bg: "#faf5ff", color: "#6b46c1", pieColor: "#9f7aea" },
};

const STATUS_OPTIONS = [
  { label: "Todos", value: "" },
  ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ label: v.label, value: k }))
];

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => { try { return format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR }); } catch { return d; } };
const fmtDateTime = (d) => { try { return format(new Date(d), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return "—"; } };

export default function RelatorioAgendamentos() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 7)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [sortBy, setSortBy] = useState("startDatetime");
  const [sortDir, setSortDir] = useState("desc");

  const getDateRange = () => {
    if (period === "custom") return { start: customStart, end: customEnd };
    if (period === "next7") return { start: fmt(new Date()), end: fmt(subDays(new Date(), -7)) };
    const days = Number(period);
    return { start: fmt(subDays(new Date(), days)), end: fmt(new Date()) };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = { dateStart: start, dateEnd: end, pageSize: 300 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/appointments", { params });
      setAgendamentos(Array.isArray(data) ? data : data?.appointments || data?.data || []);
    } catch {
      setAgendamentos([]);
    }
    setLoading(false);
  }, [period, customStart, customEnd, statusFilter]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period, statusFilter]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const filtered = agendamentos
    .filter(a => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (a.title || "").toLowerCase().includes(q) || (a.contact?.name || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va = a[sortBy] ?? "", vb = b[sortBy] ?? "";
      if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const total = agendamentos.length;
  const confirmados = agendamentos.filter(a => a.status === "confirmed").length;
  const concluidos = agendamentos.filter(a => a.status === "completed").length;
  const cancelados = agendamentos.filter(a => a.status === "cancelled").length;
  const noShow = agendamentos.filter(a => a.status === "no_show").length;
  const taxaComparecimento = (total - cancelados) > 0 ? Math.round((concluidos / (total - cancelados)) * 100) : 0;

  const totalMinutes = agendamentos
    .filter(a => a.status === "completed")
    .reduce((s, a) => s + (a.durationMinutes || 60), 0);
  const avgDuration = concluidos > 0 ? Math.round(totalMinutes / concluidos) : 0;

  const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, k) => {
    acc[k] = agendamentos.filter(a => a.status === k).length;
    return acc;
  }, {});

  const pieData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label, value: v, color: STATUS_CONFIG[k]?.pieColor }));

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "";
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((a, i) => {
      const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.scheduled;
      return `<tr>
        <td>${i + 1}</td>
        <td>${a.title || "—"}</td>
        <td>${a.contact?.name || "—"}</td>
        <td><span style="padding:2px 10px;border-radius:12px;background:${sc.bg};color:${sc.color};font-weight:600">${sc.label}</span></td>
        <td>${a.durationMinutes || 60}min</td>
        <td>${a.startDatetime ? fmtDateTime(a.startDatetime) : "—"}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Agendamentos</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#38b2ac;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>📅 Relatório de Agendamentos</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Título</th><th>Cliente</th><th>Status</th><th>Duração</th><th>Data/Hora</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">${total} agendamentos · Taxa de comparecimento: ${taxaComparecimento}%</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-agendamentos-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Agendamentos</Typography>
          <Typography className={classes.subtitle}>Compromissos, status e taxa de comparecimento</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !filtered.length}
            style={{ backgroundColor: "#38b2ac", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 130px" }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#38b2ac", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        <TextField placeholder="Buscar título ou cliente..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ flex: "1 1 160px" }} />
        {loading && <CircularProgress size={20} />}
        {!loading && agendamentos.length > 0 && (
          <Chip label={`${total} agendamentos · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#e6fffa", color: "#234e52", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        {[
          { label: "Total", value: total, sub: "Todos os agendamentos", bg: "linear-gradient(135deg,#e6fffa,#b2f5ea)", border: "#38b2ac", textColor: "#234e52", Icon: EventIcon },
          { label: "Confirmados", value: confirmados, sub: "Confirmação do cliente", bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38a169", textColor: "#276749", Icon: CheckCircleIcon },
          { label: "Concluídos", value: concluidos, sub: "Atendimento realizado", bg: "linear-gradient(135deg,#fffff0,#fefcbf)", border: "#d69e2e", textColor: "#744210", Icon: EventAvailableIcon },
          { label: "Cancelados", value: cancelados, sub: "Pelo cliente ou sistema", bg: "linear-gradient(135deg,#fff5f5,#fed7d7)", border: "#e53e3e", textColor: "#c53030", Icon: CancelIcon },
          { label: "Não Compareceu", value: noShow, sub: "No-show", bg: "linear-gradient(135deg,#faf5ff,#e9d8fd)", border: "#9f7aea", textColor: "#6b46c1", Icon: CancelIcon },
          { label: "Taxa Comparecimento", value: `${taxaComparecimento}%`, sub: `Duração média: ${avgDuration}min`, bg: "linear-gradient(135deg,#ebf8ff,#bee3f8)", border: "#4299e1", textColor: "#2b6cb0", Icon: AccessTimeIcon },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card className={classes.metricCard} elevation={0}>
              <Box className={classes.metricInner} style={{ background: m.bg, borderTop: `3px solid ${m.border}` }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography className={classes.metricLabel} style={{ color: m.textColor }}>{m.label}</Typography>
                  <m.Icon style={{ color: m.border, fontSize: 22, opacity: 0.8 }} />
                </Box>
                <Typography className={classes.metricValue} style={{ color: m.textColor }}>
                  {loading ? "—" : m.value}
                </Typography>
                <Typography className={classes.metricSub} style={{ color: m.textColor }}>{m.sub}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição por Status</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Agendamentos por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados no período</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [`${val} agendamento${val !== 1 ? "s" : ""}`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Contagem por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pieData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [`${val}`, "Agendamentos"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Lista de Agendamentos ({filtered.length})</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 40 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <TableSortLabel active={sortBy === "title"} direction={sortBy === "title" ? sortDir : "asc"}
                    onClick={() => handleSort("title")} style={{ color: "#fff" }}>
                    Título
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell}>Cliente</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Duração</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "startDatetime"} direction={sortBy === "startDatetime" ? sortDir : "asc"}
                    onClick={() => handleSort("startDatetime")} style={{ color: "#fff" }}>
                    Data/Hora
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" style={{ padding: 40 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" style={{ padding: 40, color: "#a0aec0" }}>Nenhum agendamento encontrado</TableCell></TableRow>
              ) : filtered.map((a, i) => {
                const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.scheduled;
                const clientName = a.contact?.name || a.client?.name || "—";
                return (
                  <TableRow key={a.id} className={classes.tableRow}>
                    <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{a.title || "Sem título"}</Typography>
                      {a.description && (
                        <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{a.description.slice(0, 50)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: 13 }}>{clientName}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sc.label} size="small"
                        style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, minWidth: 100 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 13, color: "#718096" }}>{a.durationMinutes || 60}min</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>
                        {a.startDatetime ? fmtDateTime(a.startDatetime) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
